import request from '@/utils/request'

/**
 * @typedef {Object} LeaseSchemeQueryParams
 * @property {number} [pageNum] - 页码
 * @property {number} [pageSize] - 每页数量
 * @property {number} [id] - 方案ID
 * @property {string} [name] - 方案名称（模糊匹配）
 * @property {number} [brandId] - 品牌ID
 * @property {number} [seriesId] - 车系ID
 * @property {number} [modelId] - 车型ID
 * @property {number} [age] - 车龄 0:全新车 1/2/3/5/8
 * @property {number} [carCert] - 是否有车证 0:否 1:是
 * @property {number} [rentCategory] - 租赁类别 1:租车 2:以租代购 4:司机合作
 * @property {number} [payMode] - 收款方式 4:月付(满月) 5:月付(自然月) 12:周付(自然周) 10:日付
 * @property {number} [payPlanType] - 收款方案 0:固定 2:动态
 * @property {string[]} [cityCodes] - 城市code列表
 * @property {number} [hasActivity] - 是否绑定活动 0:否 1:是
 * @property {string[]} [storeIds] - 门店ID列表
 * @property {number} [state] - 方案状态 0:下架 10:上架
 */

/**
 * @typedef {Object} LeaseSchemeItem
 * @property {number} id - 方案ID
 * @property {string} name - 方案名称
 * @property {number} brandId - 品牌ID
 * @property {string} brandName - 品牌名称
 * @property {number} seriesId - 车系ID
 * @property {string} seriesName - 车系名称
 * @property {number} modelId - 车型ID
 * @property {string} modelName - 车型名称
 * @property {number} rentPeriod - 租赁周期
 * @property {number} rentPeriodType - 租期单位 1:日 2:周 3:月
 * @property {number} payMode - 收款方式
 * @property {string} rentAmount - 每期租金
 * @property {string} deposit - 押金
 * @property {string[]} showCityCodes - 方案展示归属城市
 * @property {number} age - 车龄
 * @property {number} carCert - 是否有车证 0:否 1:是
 * @property {number} rentCategory - 租赁类别
 * @property {number} templateId - 合同模板ID
 * @property {string} templateName - 合同模板名称
 * @property {number} templateState - 合同模板状态
 * @property {number} state - 方案状态 0:下架 10:上架
 */

/**
 * @typedef {Object} LeaseSchemeListResponse
 * @property {number} pageNum
 * @property {number} pageSize
 * @property {number} totalNum
 * @property {number} totalPage
 * @property {LeaseSchemeItem[]} items
 */

/**
 * 获取商城租赁方案列表
 * @param {LeaseSchemeQueryParams} data - 查询参数
 * @returns {Promise<{code: number, data: LeaseSchemeListResponse, msg: string}>}
 */
export function getLeaseSchemeList(data) {
  return request({
    url: '/admin/v1/vehicle/mallRentPlan/list',
    method: 'POST',
    data,
  })
}

/**
 * 租赁方案简单列表（不分页，用于选择关联方案）
 * @param {Object} [data] - 查询参数（可选筛选条件）
 * @returns {Promise<{code: number, data: Array<{id: number, name: string}>, msg: string}>}
 */
export function getLeaseSchemeListSimple(data) {
  return request({
    url: '/admin/v1/vehicle/mallRentPlan/listSimple',
    method: 'POST',
    data,
  })
}

/**
 * @typedef {Object} LeaseSchemeDetail
 * @property {number} id - 方案ID
 * @property {string} name - 方案名称
 * @property {number} state - 方案状态 0:下架 10:上架
 * @property {number} orderType - 订单类型 1:个人 2:企业
 * @property {string} gmtCreate - 创建时间
 * @property {string} brandId - 品牌ID
 * @property {string} brandName - 品牌名称
 * @property {string} seriesId - 车系ID
 * @property {string} seriesName - 车系名称
 * @property {number} modelId - 车型ID
 * @property {string} modelName - 车型名
 * @property {string[]} cityCodes - 方案所属城市code
 * @property {string[]} cityNames - 方案所属城市名称
 * @property {number} age - 车龄
 * @property {number} carCert - 是否有车证 0:否 1:是
 * @property {number} rentCategory - 租赁类别 1:租车 2:以租代购 3:司机合作
 * @property {number} rentPeriod - 租赁周期
 * @property {number} rentPeriodType - 租期单位 1:日 2:周 3:月
 * @property {number} payMode - 支付方式
 * @property {number} templateId - 合同模板ID
 * @property {string} templateName - 合同模板名
 * @property {number} templateState - 合同模板状态
 * @property {Object[]} orderPlanFeeList - 支付项配置列表
 * @property {Object} payPlanFee - 司机合作规则(rentCategory=3时)
 * @property {{ entityId: number, entityName: string }} entityInfo - 主体信息
 * @property {{ storeId: number, storeName: string }[]} storeList - 门店列表
 * @property {Array<{
 *   activityId: number,
 *   activityName: string,
 *   preFreeRentDays: string,
 *   presentDays: string,
 *   startDate: string,
 *   endDate: string
 * }>} activityList - 关联活动列表
 */

/**
 * 获取商城租赁方案详情
 * @param {{ id: number }} data - 方案ID
 * @returns {Promise<{code: number, data: LeaseSchemeDetail, msg: string}>} 方案详情
 */
export function getLeaseSchemeDetail(data) {
  return request({
    url: '/admin/v1/vehicle/mallRentPlan/detail',
    method: 'POST',
    data,
  })
}

/**
 * @typedef {Object} RentFeeItem
 * @property {number} fundType - 支付类型 1:租金 2:押金 3:管理费 4:服务费
 *   5:维保费用 6:出险费用 7:违章代办 8:油费 9:违约金 10:车损 11:免佣 12:押金退款 100:其他
 * @property {number} payMode - 付款方式 1:年付 2:半年一付 3:周付 4:月付(满月) 5:月付(自然月) 6:一次性
 * @property {number} amountAccuracy - 保留位数 0:取整 2:两位小数
 * @property {number} payPlanType - 收款金额方案 0:固定 1:阶梯 2:动态
 * @property {number} amountType - 金额类型 1:每期总金额 2:日租金
 * @property {string} amount - 押金的总金额/租金及其它费项的每期金额
 * @property {string} [depositPeriodAmount] - 押金每期金额
 * @property {string} [spId] - 完单网约平台
 * @property {number} [dynamicType] - 动态类型 1:完单量
 * @property {Object[]} [dynamicList] - 动态配置 [{ startValue, endValue, amount }]
 * @property {Object[]} [depositInstallmentList] - 押金分期 [{ numberOfPeriods, presentAmount }]
 * @property {number} advancePayment - 发车前收款 0:不收 1:首期 2:全部
 */

/**
 * @typedef {Object} LeaseSchemeSaveParams
 * @property {number} [id] - 方案ID（编辑时传，新增不传）
 * @property {string} name - 方案名称
 * @property {number} modelId - 车型ID
 * @property {string[]} cityCodes - 方案所属城市
 * @property {number} age - 车龄
 * @property {number} carCert - 是否有车证 0:否 1:是
 * @property {number} rentCategory - 租赁类别 1:租车 2:以租代购 3:司机合作
 * @property {number} rentPeriod - 租赁周期
 * @property {number} rentPeriodType - 租期单位 1:日 2:周 3:月
 * @property {number} payMode - 支付方式
 * @property {number} templateId - 合同模板ID
 * @property {RentFeeItem[]} rentFeeList - 支付项列表
 * @property {Object} [payPlanFee] - 司机合作规则（rentCategory=3时必填）
 * @property {number[]} activityIdList - 活动ID列表
 * @property {number[]} storeIdList - 门店ID列表
 * @property {Object} rentCarRule - 租车须知
 * @property {Object} returnCarRule - 还车说明
 * @property {Object} rentCarRuleTip - 租车须知提示
 * @property {Object} returnCarRuleTip - 还车说明提示
 */

/**
 * 保存前检查（9维排重 + 业务规则校验）
 * @param {LeaseSchemeSaveParams} data - 方案数据
 * @returns {Promise<{code: number, data: boolean, msg: string}>} 检查结果
 */
export function checkBeforeSave(data) {
  return request({
    url: '/admin/v1/vehicle/mallRentPlan/checkBeforeSave',
    method: 'POST',
    data,
  })
}

/**
 * 保存商城租赁方案（新增/编辑）
 * @param {LeaseSchemeSaveParams} data - 方案数据，传 id 为编辑，不传为新增
 * @returns {Promise<{code: number, data: number, msg: string}>} data 为方案ID
 */
export function saveLeaseScheme(data) {
  return request({
    url: '/admin/v1/vehicle/mallRentPlan/save',
    method: 'POST',
    data,
  })
}

/**
 * 商城租赁方案上下架
 * @param {{ id: number, state: number }} data - id: 方案ID, state: 0=下架 10=上架
 * @returns {Promise<{code: number, data: boolean, msg: string}>}
 */
export function updateLeaseSchemeState(data) {
  return request({
    url: '/admin/v1/vehicle/mallRentPlan/updateState',
    method: 'POST',
    data,
  })
}

/**
 * 删除前校验 - 检查是否存在商城已下单未签约的订单
 * @param {{ id: number }} data - 方案ID
 * @returns {Promise<{code: number, data: { canDelete: boolean, reason?: string }, msg: string}>}
 */
export function checkBeforeDelete(data) {
  return request({
    url: '/admin/v1/vehicle/mallRentPlan/checkBeforeDelete',
    method: 'POST',
    data,
  })
}

/**
 * 删除商城租赁方案
 * @param {{ id: number }} data - 方案ID
 * @returns {Promise<{code: number, data: boolean, msg: string}>}
 * @description 存在未签约订单时后端返回错误，前端透传 msg
 */
export function deleteLeaseScheme(data) {
  return request({
    url: '/admin/v1/vehicle/mallRentPlan/delete',
    method: 'POST',
    data,
  })
}


/**
 * @typedef {Object} EnterprisePlanQueryParams
 * @property {number} [pageNum] - 页码
 * @property {number} [pageSize] - 每页数量
 * @property {number} [id] - 方案ID
 * @property {string} [name] - 方案名称（模糊匹配）
 * @property {number} [brandId] - 品牌ID
 * @property {number} [seriesId] - 车系ID
 * @property {number} [modelId] - 车型ID
 * @property {number} [state] - 方案状态 0:下架 10:上架
 */

/**
 * @typedef {Object} EnterprisePlanItem
 * @property {number} id - 方案ID
 * @property {string} name - 方案名称
 * @property {number} brandId - 品牌ID
 * @property {string} brandName - 品牌名称
 * @property {number} seriesId - 车系ID
 * @property {string} seriesName - 车系名称
 * @property {number} modelId - 车型ID
 * @property {string} modelName - 车型名称
 * @property {number} rentCategory - 租赁类别 1:租车 2:以租代购
 * @property {number} rentPeriod - 租赁周期
 * @property {number} rentPeriodType - 租期单位 1:日 2:周 3:月
 * @property {string} rentAmount - 每期租金
 * @property {string} deposit - 押金
 * @property {number} state - 方案状态 0:下架 10:上架
 */

/**
 * 获取企业租赁方案列表
 * @param {EnterprisePlanQueryParams} data - 查询参数
 * @returns {Promise<{
 *   code: number,
 *   data: {
 *     pageNum: number,
 *     pageSize: number,
 *     totalNum: number,
 *     totalPage: number,
 *     items: EnterprisePlanItem[]
 *   },
 *   msg: string
 * }>}
 */
export function getEnterprisePlanList(data) {
  return request({
    url: '/admin/v1/vehicle/entRentPlan/list',
    method: 'POST',
    data,
  })
}

/**
 * @typedef {Object} EnterprisePlanDetail
 * @property {number} id - 方案ID
 * @property {string} name - 方案名称
 * @property {number} state - 方案状态 0:下架 10:上架
 * @property {number} orderType - 订单类型
 * @property {string} gmtCreate - 创建时间
 * @property {string} brandId - 品牌ID
 * @property {string} brandName - 品牌名称
 * @property {string} seriesId - 车系ID
 * @property {string} seriesName - 车系名称
 * @property {number} modelId - 车型ID
 * @property {string} modelName - 车型名称
 * @property {number} rentCategory - 租赁类别 1:租车 2:以租代购
 * @property {number} rentPeriod - 租赁周期
 * @property {number} rentPeriodType - 租期单位 1:日 2:周 3:月
 * @property {number} presentDays - 赠送天数
 * @property {Object[]} orderPlanFeeList - 支付项配置列表
 */

/**
 * 获取企业租赁方案详情
 * @param {{ id: number }} data - 方案ID
 * @returns {Promise<{code: number, data: EnterprisePlanDetail, msg: string}>}
 */
export function getEnterprisePlanDetail(data) {
  return request({
    url: '/admin/v1/vehicle/entRentPlan/detail',
    method: 'POST',
    data,
  })
}

/**
 * @typedef {Object} EnterpriseRentFeeItem
 * @property {number} fundType - 支付类型 1:租金 2:押金 3:管理费 4:服务费
 *   5:维保费用 6:出险费用 7:违章代办 8:油费 9:违约金 10:车损 11:免佣 12:押金退款 100:其他
 * @property {number} payMode - 付款方式 1:年付 2:半年一付 3:周付 4:月付(满月) 5:月付(自然月) 6:一次性
 * @property {number} dailyRule - 日租规则 0:自然月 1:30天
 * @property {number} lastPeriodRule - 尾期租金规则 0:月租减首期 1:尾期以日租金算
 * @property {number} payPlanType - 收款金额方案 0:固定 1:阶梯 2:动态
 * @property {number} amountType - 金额类型 1:每期总金额 2:日租金
 * @property {string} amount - 每期金额
 * @property {Object[]} [piecewiseList] - 阶段计费配置 [{ startMonth, endMonth, amount }]
 * @property {number} payPost - 后置缴款 0:当期 1:下期 10:自定义
 * @property {number} payDay - 缴款日
 * @property {number} advancePayment - 发车前收款 0:不收 1:首期 2:全部
 * @property {number} amountAccuracy - 保留位数 0:取整 2:两位小数
 */

/**
 * @typedef {Object} EnterprisePlanSaveParams
 * @property {number} [id] - 方案ID（编辑时传，新增不传）
 * @property {string} name - 方案名称
 * @property {number} modelId - 车型ID
 * @property {number} rentCategory - 租赁类别 1:租车 2:以租代购
 * @property {number} rentPeriod - 租赁周期
 * @property {number} rentPeriodType - 租期单位 1:日 2:周 3:月
 * @property {number} [presentDays] - 赠送天数
 * @property {EnterpriseRentFeeItem[]} rentFeeList - 支付项列表
 */

/**
 * 保存企业租赁方案（新增/编辑）
 * @param {EnterprisePlanSaveParams} data - 方案数据，传 id 为编辑，不传为新增
 * @returns {Promise<{code: number, data: number, msg: string}>} data 为方案ID
 */
export function saveEnterprisePlan(data) {
  return request({
    url: '/admin/v1/vehicle/entRentPlan/save',
    method: 'POST',
    data,
  })
}

/**
 * 企业租赁方案上架/下架
 * @param {{ id: number, state: number }} data - id: 方案ID, state: 0=下架 10=上架
 * @returns {Promise<{code: number, data: boolean, msg: string}>}
 */
export function updateEnterprisePlanState(data) {
  return request({
    url: '/admin/v1/vehicle/entRentPlan/updateState',
    method: 'POST',
    data,
  })
}

/**
 * 删除企业租赁方案
 * @param {{ id: number }} data - 方案ID
 * @returns {Promise<{code: number, data: boolean, msg: string}>}
 */
export function deleteEnterprisePlan(data) {
  return request({
    url: '/admin/v1/vehicle/entRentPlan/delete',
    method: 'POST',
    data,
  })
}
